import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import { formatDate } from '../lib/orgTimezone';
import { NewInformationRequestModal } from './NewInformationRequestModal';

/**
 * ClientInformationRequests — the client-scoped view of the outbound information
 * requests (C5 "view 2"), shown on the client profile's Portal tab beneath the
 * property-setup intake queue. Reads the SAME board endpoint as the main board,
 * narrowed to this client (GET /api/information-requests/board?client_id=). Rows
 * click through to the request profile; "View all" opens the full board pre-filtered.
 *
 * 7/28 B2 — this section is the home for CREATING an information request for the
 * client (the old "New request" on the setup queue wrongly created a property-setup
 * entry). "+ New request" opens the info-request pop-up with this client pre-filled;
 * "View all →" opens the full board. The setup queue above stays the client-initiated
 * property intake channel only.
 */

type RequestStatus = 'draft' | 'sent' | 'partial' | 'complete';

interface BoardRow {
  id: string;
  client_id: string;
  title: string;
  status: RequestStatus;
  tax_year: number;
  property_name: string;
  due_date: string | null;
  is_overdue: boolean;
  items_total: number;
  items_received: number;
  items_outstanding: number;
}

const statusVariant: Record<RequestStatus, 'neutral' | 'info' | 'warning' | 'success'> = {
  draft: 'neutral', sent: 'info', partial: 'warning', complete: 'success',
};
const statusLabel: Record<RequestStatus, string> = {
  draft: 'Draft', sent: 'Sent', partial: 'Partial', complete: 'Complete',
};

export function ClientInformationRequests({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const q = useQuery<BoardRow[]>({
    queryKey: ['information-requests-board', clientId],
    queryFn: ({ signal }) => api.get<BoardRow[]>(`/api/information-requests/board?client_id=${clientId}`, signal),
    enabled: !!clientId,
  });
  const rows = q.data ?? [];

  return (
    <div style={{ marginBottom: 'var(--sp-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Information requests</h3>
          <p style={{ margin: '2px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            Documents the firm has requested from this client. Use <b>New request</b> to ask this client for documents — it opens a pop-up with this client pre-filled.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexShrink: 0 }}>
          <a
            href={`/information-requests?client_id=${clientId}`}
            onClick={(e) => { e.preventDefault(); navigate(`/information-requests?client_id=${clientId}`); }}
            style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap' }}
          >View all →</a>
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>+ New request</Button>
        </div>
      </div>

      {q.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>
      ) : q.isError ? (
        <div style={{ padding: 'var(--sp-5)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', borderRadius: 'var(--r-lg)', color: 'var(--color-status-red)', textAlign: 'center', fontSize: 'var(--fs-sm)' }}>
          Couldn't load information requests. Refresh to try again.
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 'var(--sp-5)', background: 'var(--color-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-sm)' }}>
          No information requests for this client yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {rows.map((r) => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/information-requests/${r.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/information-requests/${r.id}`); }}
              style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-3) var(--sp-4)', cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{r.title}</span>
                <Badge variant={statusVariant[r.status] ?? 'neutral'} dot>{statusLabel[r.status] ?? r.status}</Badge>
                {r.is_overdue && <Badge variant="warning" dot>Overdue</Badge>}
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginTop: 2 }}>
                {r.property_name} · {r.items_received}/{r.items_total} received{r.due_date ? ` · due ${formatDate(r.due_date, { dateOnly: true })}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      <NewInformationRequestModal
        open={createOpen}
        defaultClientId={clientId}
        onClose={() => setCreateOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ['information-requests-board', clientId] })}
      />
    </div>
  );
}
